/**
 * Geocoding und Gebäudedaten. Alle Aufrufe gehen direkt vom Browser an die
 * Dienste (Auftrag V6); der eigene Server sieht Adressen und EGID nie.
 *
 *  - geo.admin.ch SearchServer: amtliche Adressen, Orte, Gemeinden (Schweiz).
 *  - geo.admin.ch MapServer (ch.bfs.gebaeude_wohnungs_register): GWR, EGID.
 *  - geo.admin.ch Height-Service: swissALTI3D-Höhe (LV95).
 *  - Open-Meteo Geocoding API: Orte weltweit (GeoNames, CC BY 4.0).
 */
import type { Building, GwrInfo } from '../core/domain/types';
import { roughlyInSwitzerland, wgs84ToLv95 } from './lv95';
import codes from './gwr-codes.json';

export const GEOADMIN = 'https://api3.geo.admin.ch/rest/services';
export const OM_GEOCODING = 'https://geocoding-api.open-meteo.com/v1/search';
const GWR_LAYER = 'ch.bfs.gebaeude_wohnungs_register';

export class ServiceError extends Error {
  constructor(
    message: string,
    public kind: 'network' | 'http' | 'format' | 'not-found',
  ) {
    super(message);
  }
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, { signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ServiceError('Dienst nicht erreichbar. Netzverbindung prüfen.', 'network');
  }
  if (!res.ok) throw new ServiceError(`Dienst antwortet mit Fehler ${res.status}.`, 'http');
  try {
    return (await res.json()) as T;
  } catch {
    throw new ServiceError('Antwort des Dienstes nicht lesbar.', 'format');
  }
}

const strip = (s: string) => s.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

export interface Candidate {
  id: string;
  source: 'geo.admin.ch' | 'open-meteo-geocoding' | 'koordinaten';
  kind: 'Adresse' | 'Gemeinde' | 'PLZ' | 'Ort' | 'Gebäude (EGID)' | 'Koordinaten';
  label: string;
  sub: string;
  lat: number;
  lon: number;
  egid?: string;
  featureId?: string;
  timezone?: string;
  country?: string;
}

const ORIGIN_KIND: Record<string, Candidate['kind']> = { address: 'Adresse', gg25: 'Gemeinde', zipcode: 'PLZ', gazetteer: 'Ort' };

interface SearchResult {
  results: { id: number; attrs: { label: string; origin: string; lat: number; lon: number; featureId?: string; detail?: string } }[];
}

export async function searchSwiss(text: string, signal?: AbortSignal): Promise<Candidate[]> {
  const q = new URLSearchParams({ searchText: text, type: 'locations', origins: 'address,zipcode,gg25,gazetteer', limit: '10', sr: '4326', lang: 'de' });
  const r = await getJson<SearchResult>(`${GEOADMIN}/api/SearchServer?${q}`, signal);
  return (r.results ?? []).map((x) => {
    const label = strip(x.attrs.label);
    const kind = ORIGIN_KIND[x.attrs.origin] ?? 'Ort';
    const egid = x.attrs.origin === 'address' && x.attrs.featureId ? x.attrs.featureId.split('_')[0] : undefined;
    return {
      id: `ga-${x.id}-${x.attrs.origin}`,
      source: 'geo.admin.ch',
      kind,
      label,
      sub: `${kind} · ${x.attrs.lat.toFixed(5)}, ${x.attrs.lon.toFixed(5)}${egid ? ` · EGID ${egid}` : ''}`,
      lat: x.attrs.lat,
      lon: x.attrs.lon,
      egid,
      featureId: x.attrs.origin === 'address' ? x.attrs.featureId : undefined,
      timezone: 'Europe/Zurich',
      country: 'CH',
    } satisfies Candidate;
  });
}

interface OmGeoResult {
  results?: { id: number; name: string; latitude: number; longitude: number; elevation?: number; timezone?: string; country?: string; country_code?: string; admin1?: string; admin2?: string; postcodes?: string[] }[];
}

export async function searchWorld(text: string, signal?: AbortSignal): Promise<Candidate[]> {
  const q = new URLSearchParams({ name: text, count: '8', language: 'de', format: 'json' });
  const r = await getJson<OmGeoResult>(`${OM_GEOCODING}?${q}`, signal);
  return (r.results ?? []).map((x) => {
    const parts = [x.admin2, x.admin1, x.country].filter((p, i, a): p is string => !!p && a.indexOf(p) === i);
    return {
      id: `om-${x.id}`,
      source: 'open-meteo-geocoding',
      kind: 'Ort',
      label: x.name,
      sub: `${parts.join(', ')} · ${x.latitude.toFixed(4)}, ${x.longitude.toFixed(4)}`,
      lat: x.latitude,
      lon: x.longitude,
      timezone: x.timezone,
      country: x.country_code,
    } satisfies Candidate;
  });
}

interface FindResult {
  results: { featureId: string; geometry?: { x: number; y: number }; attributes: Record<string, unknown> }[];
}

function addressFromGwr(a: Record<string, unknown>): string {
  const street = (a.strname_deinr as string) || '';
  const plz = a.dplz4 ? String(a.dplz4) : '';
  const place = (a.dplzname as string) || (a.ggdename as string) || '';
  return [street, [plz, place].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

export async function findEgid(egid: string, signal?: AbortSignal): Promise<Candidate[]> {
  const q = new URLSearchParams({ layer: GWR_LAYER, searchText: egid, searchField: 'egid', returnGeometry: 'true', contains: 'false', sr: '4326', lang: 'de' });
  const r = await getJson<FindResult>(`${GEOADMIN}/ech/MapServer/find?${q}`, signal);
  return (r.results ?? [])
    .filter((x) => x.geometry && String(x.attributes.egid) === egid)
    .map((x) => {
      const label = addressFromGwr(x.attributes) || `Gebäude EGID ${egid}`;
      return {
        id: `egid-${x.featureId}`,
        source: 'geo.admin.ch',
        kind: 'Gebäude (EGID)',
        label,
        sub: `EGID ${egid} · ${x.geometry!.y.toFixed(5)}, ${x.geometry!.x.toFixed(5)}`,
        lat: x.geometry!.y,
        lon: x.geometry!.x,
        egid,
        featureId: x.featureId,
        timezone: 'Europe/Zurich',
        country: 'CH',
      } satisfies Candidate;
    });
}

const num = (v: unknown): number | null => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

export function parseGwr(a: Record<string, unknown>): GwrInfo {
  return {
    egid: String(a.egid),
    gbauj: num(a.gbauj),
    gbaup: num(a.gbaup),
    gkat: num(a.gkat),
    gklas: num(a.gklas),
    gstat: num(a.gstat),
    gastw: num(a.gastw),
    gebf: num(a.gebf),
    garea: num(a.garea),
    gwaerzh1: num(a.gwaerzh1),
    genh1: num(a.genh1),
    gwaerzw1: num(a.gwaerzw1),
    genw1: num(a.genw1),
    gexpdat: typeof a.gexpdat === 'string' ? a.gexpdat : null,
    fetchedAt: new Date().toISOString(),
  };
}

export async function fetchGwr(featureIdOrEgid: string, signal?: AbortSignal): Promise<GwrInfo | null> {
  const fid = featureIdOrEgid.includes('_') ? featureIdOrEgid : `${featureIdOrEgid}_0`;
  try {
    const r = await getJson<{ feature?: { attributes: Record<string, unknown> } }>(`${GEOADMIN}/ech/MapServer/${GWR_LAYER}/${encodeURIComponent(fid)}?returnGeometry=false&lang=de`, signal);
    return r.feature ? parseGwr(r.feature.attributes) : null;
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    return null;
  }
}

export async function swissHeight(lat: number, lon: number, signal?: AbortSignal): Promise<number | null> {
  const { e, n } = wgs84ToLv95(lat, lon);
  try {
    const r = await getJson<{ height?: string }>(`${GEOADMIN}/height?easting=${e.toFixed(1)}&northing=${n.toFixed(1)}&sr=2056`, signal);
    const h = Number(r.height);
    // Ausserhalb des Modells liefert der Dienst 0 oder einen Fehler.
    return Number.isFinite(h) && h > 0 ? h : null;
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    return null;
  }
}

export const newId = () => `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** Wandelt einen Treffer in ein Gebäude um (Höhe und GWR werden ergänzt). */
export async function candidateToBuilding(c: Candidate, labelOverride?: string): Promise<Building> {
  const inCh = c.country === 'CH' || (c.source !== 'open-meteo-geocoding' && roughlyInSwitzerland(c.lat, c.lon));
  const b: Building = {
    id: newId(),
    label: labelOverride || c.label,
    address: c.kind === 'Adresse' || c.kind === 'Gebäude (EGID)' ? c.label : undefined,
    egid: c.egid,
    country: c.country,
    lat: c.lat,
    lon: c.lon,
    timezone: c.timezone,
    geocoder: c.source === 'koordinaten' ? 'koordinaten' : c.source,
    addedAt: new Date().toISOString(),
  };
  if (inCh) {
    b.lv95 = wgs84ToLv95(c.lat, c.lon);
    const [h, gwr] = await Promise.all([swissHeight(c.lat, c.lon), c.egid ? fetchGwr(c.featureId ?? c.egid) : Promise.resolve(null)]);
    if (h !== null) b.elevation = { value: h, source: 'swissALTI3D' };
    if (gwr) b.gwr = gwr;
    if (!b.timezone) b.timezone = 'Europe/Zurich';
  }
  return b;
}

type CodeTable = Record<string, Record<string, string>>;
const CODES = (codes as { codes: CodeTable; source: string }).codes;
export const GWR_CODE_SOURCE = (codes as { source: string }).source;

export function gwrLabel(field: 'GKAT' | 'GKLAS' | 'GBAUP' | 'GSTAT' | 'GWAERZH1' | 'GENH1' | 'GWAERZW1' | 'GENW1', code: number | null): string {
  if (code === null) return 'nicht erfasst';
  return CODES[field]?.[String(code)] ?? `Code ${code}`;
}
